import React, { useEffect, useMemo, useState } from "react";
import DatePicker from "react-datepicker";

import {
    asString,
    useClearFieldError,
    useFieldError,
    useRegisterFieldValidation,
} from "../core/formValidation";
import { createDateValidator } from "../validation/defaultValidators";

export interface DatePickerFieldProps {
    label: string;
    name: string;
    daysInFuture: number;
    allowedWeekdays?: number[];

    defaultValue?: Date | null;
    placeholder?: string;
    required?: boolean;
    disabled?: boolean;
    readOnly?: boolean;
    className?: string;
    validate?: (value: string) => boolean;
    validationMessage?: string;
}

export const DatePickerField: React.FC<DatePickerFieldProps> = ({
    label,
    name,
    daysInFuture,
    allowedWeekdays = [1, 2, 3, 4],
    defaultValue = null,
    placeholder = "Datum auswaehlen",
    required = false,
    disabled = false,
    readOnly = false,
    className,
    validate,
    validationMessage,
}) => {
    const [selectedDate, setSelectedDate] =
        useState<Date | null>(defaultValue);

    const registerFieldValidation = useRegisterFieldValidation();
    const clearFieldError = useClearFieldError();
    const fieldError = useFieldError(name);

    const effectiveValidator = useMemo(
        () => validate ?? createDateValidator(required),
        [required, validate]
    );

    useEffect(() => {
        if (!registerFieldValidation || disabled) {
            return;
        }

        return registerFieldValidation({
            name,
            message: validationMessage,
            validate: (value) => effectiveValidator(asString(value)),
        });
    }, [
        disabled,
        effectiveValidator,
        name,
        registerFieldValidation,
        validationMessage,
    ]);

    const minDate = useMemo(() => {
        const date = new Date();
        date.setHours(0, 0, 0, 0);
        return date;
    }, []);

    const maxDate = useMemo(() => {
        const date = new Date(minDate);
        date.setDate(date.getDate() + daysInFuture);
        return date;
    }, [minDate, daysInFuture]);

    const filterDate = (date: Date): boolean =>
        allowedWeekdays.includes(date.getDay());

    const handleChange = (date: Date | null): void => {
        setSelectedDate(date);
        clearFieldError(name);
    };

    return (
        <div className="form-field">
            <label htmlFor={name}>{label}</label>

            <span className="wpcf7-form-control-wrap" data-name={name}>
                <DatePicker
                    id={name}
                    selected={selectedDate}
                    onChange={handleChange}
                    minDate={minDate}
                    maxDate={maxDate}
                    filterDate={filterDate}
                    dateFormat="dd.MM.yyyy"
                    placeholderText={placeholder}
                    className={[
                        "input",
                        "input--date",
                        fieldError ? "wpcf7-not-valid" : null,
                        className,
                    ]
                        .filter(Boolean)
                        .join(" ")}
                    disabled={disabled}
                    readOnly={readOnly}
                />

                <input
                    type="hidden"
                    name={name}
                    value={selectedDate?.toISOString() ?? ""}
                    required={required}
                    disabled={disabled}
                />

                {fieldError ? (
                    <span className="wpcf7-not-valid-tip" aria-hidden="true">
                        {fieldError}
                    </span>
                ) : null}
            </span>
        </div>
    );
};

